import { useState } from "react";
import type {
  DataForwarderConnectionTestStatus,
  DataForwarderRegisterPhase,
} from "../types/preferences_types";
import {
  useCreatePolicyStep1Mutation,
  useDataForwarderPoliciesQuery,
  useDeletePoliciesMutation,
  useOrganizationsQuery,
  useRegionsQuery,
  useTestConnectionMutation,
  useUpdatePolicyStep1Mutation,
  useUpdatePolicyStep2Mutation,
  type ConnectionTestRequest,
  type CreatePolicyStep1Request,
  type UpdatePolicyStep1Request,
  type UpdatePolicyStep2Request,
  type UpdatePolicyStep2S3Request,
  type UpdatePolicyStep2GCSRequest,
} from "../api/dataForwarderPolicies";
import { queryClient } from "@/shared/libs/queryClient";
import { queryKeys } from "@/shared/libs/queryClient";
import { CHANNELS } from "@/constants/channels";
import { useMemo } from "react";
import { md5 } from "js-md5";

export function useDataForwarderPolicies() {
  // React Query hooks
  const { data: policies = [], isLoading, refetch } = useDataForwarderPoliciesQuery();
  const { data: regions = [] } = useRegionsQuery();
  const { data: organizations = [] } = useOrganizationsQuery();
  const createStep1Mutation = useCreatePolicyStep1Mutation();
  const updateStep1Mutation = useUpdatePolicyStep1Mutation();
  const updateStep2Mutation = useUpdatePolicyStep2Mutation();
  const testConnectionMutation = useTestConnectionMutation();
  const deleteMutation = useDeletePoliciesMutation();

  const [errorMsg, setErrorMsg] = useState("");

  // wizard state
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [editingId, setEditingId] = useState<number | null>(null);

  // form fields (string 유지)
  const [policyName, setPolicyName] = useState("");
  const [orgId, setOrgId] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [channel, setChannel] = useState("");
  const [subChannel, setSubChannel] = useState("");
  const [dwAccountId, setDwAccountId] = useState("");
  const [reports, setReports] = useState("");
  const [externalId, setExternalId] = useState("");

  const [forwardingType, setForwardingType] = useState<"s3" | "gcs">("s3");

  // S3 fields
  const [bucketRegion, setBucketRegion] = useState("");
  const [bucketName, setBucketName] = useState("");
  const [roleArn, setRoleArn] = useState("");
  const [prefix, setPrefix] = useState("");

  // GCS fields
  const [targetServiceAccount, setTargetServiceAccount] = useState("");

  const [connectionTestStatus, setConnectionTestStatus] =
    useState<DataForwarderConnectionTestStatus>("");
  const [connectionTestMessage, setConnectionTestMessage] = useState("");

  const channels = useMemo<string[]>(() => [...CHANNELS], []);

  const resetForm = () => {
    setErrorMsg("");
    setStep(1);
    setMode("create");
    setEditingId(null);
    setPolicyName("");
    setOrgId("");
    setWorkspaceId("");
    setChannel("");
    setSubChannel("");
    setDwAccountId("");
    setReports("");
    setExternalId("");
    setForwardingType("s3");
    setBucketRegion("");
    setBucketName("");
    setRoleArn("");
    setPrefix("");
    setTargetServiceAccount("");
    setConnectionTestStatus("");
    setConnectionTestMessage("");
  };

  const reload = async () => {
    setErrorMsg("");
    await refetch();
  };

  const openCreate = () => {
    resetForm();
    setMode("create");
    setOpen(true);
  };

  const openEdit = (id: number) => {
    resetForm();
    const p = policies.find((x) => x.id === id);
    if (!p) return;

    setMode("edit");
    setEditingId(id);

    setPolicyName(p.policy_name ?? "");
    setOrgId(p.org_id ?? "");
    setWorkspaceId(p.workspace_id ?? "");
    setChannel(p.channel ?? "");
    setSubChannel(p.sub_channel ?? "");
    setDwAccountId(p.dw_account_id ?? "");
    setReports(p.reports ?? "");

    setForwardingType(p.forwarding_type ?? "s3");

    // destination_info에서 필드 추출 (JSON 문자열일 수 있으므로 파싱)
    const rawDestInfo = p.destination_info;
    const destInfo: Record<string, string> | null =
      typeof rawDestInfo === "string"
        ? (() => {
            try {
              return JSON.parse(rawDestInfo) as Record<string, string>;
            } catch {
              return null;
            }
          })()
        : ((rawDestInfo as Record<string, string>) ?? null);

    if (destInfo && p.forwarding_type === "s3") {
      setExternalId(destInfo.external_id || md5((p.policy_name ?? "").trim()));
      setBucketRegion(destInfo.bucket_region ?? "");
      setBucketName(destInfo.bucket_name ?? "");
      setRoleArn(destInfo.role_arn ?? "");
      setPrefix(destInfo.prefix ?? destInfo.s3_path_prefix ?? "");
    } else if (destInfo && p.forwarding_type === "gcs") {
      setBucketName(destInfo.bucket_name ?? "");
      setTargetServiceAccount(destInfo.target_service_account ?? "");
      setPrefix(destInfo.prefix ?? "");
    } else {
      // destination_info가 없는 경우 (1단계까지만 진행된 정책)
      setExternalId(md5((p.policy_name ?? "").trim()));
    }

    const phase = p.register_phase ?? 1;
    const nextStep = Math.min((phase + 1) as DataForwarderRegisterPhase, 3);
    setStep(nextStep as 1 | 2 | 3);

    setOpen(true);
  };

  // Channel 변경 시 의존 필드 초기화
  const handleChannelChange = (value: string) => {
    setChannel(value);
    if (!value) {
      setSubChannel("");
      setDwAccountId("");
      setReports("");
    }
  };

  const validateStep1 = () => {
    setErrorMsg("");
    if (!policyName.trim()) return (setErrorMsg("정책 이름을 입력해주세요."), false);
    if (!orgId.trim()) return (setErrorMsg("ORG ID를 입력해주세요."), false);
    return true;
  };

  const validateStep2 = () => {
    setErrorMsg("");
    if (!forwardingType) return (setErrorMsg("전송 타입을 선택해주세요."), false);
    if (!bucketName.trim()) return (setErrorMsg("버킷 이름을 입력해주세요."), false);

    if (forwardingType === "s3") {
      if (!bucketRegion.trim()) return (setErrorMsg("S3 버킷 리전을 선택해주세요."), false);
      if (!roleArn.trim()) return (setErrorMsg("AWS Role ARN을 입력해주세요."), false);
      if (!roleArn.startsWith("arn:aws:iam::"))
        return (
          setErrorMsg(
            "올바른 AWS Role ARN 형식이 아닙니다. (예: arn:aws:iam::{account_id}:role/{role_name})"
          ),
          false
        );
    } else if (forwardingType === "gcs") {
      if (!targetServiceAccount.trim())
        return (setErrorMsg("Target Service Account를 입력해주세요."), false);
    }

    return true;
  };

  const saveStep1 = async (): Promise<boolean> => {
    // create or update step1
    if (!validateStep1()) return false;

    const request: CreatePolicyStep1Request | UpdatePolicyStep1Request = {
      policy_name: policyName.trim(),
      org_id: orgId.trim(),
      workspace_id: workspaceId.trim() || undefined,
      channel: channel.trim() || undefined,
      sub_channel: subChannel.trim() || undefined,
      dw_account_id: dwAccountId.trim() || undefined,
      reports: reports.trim() || undefined,
    };

    try {
      if (mode === "create") {
        const result = await createStep1Mutation.mutateAsync({
          ...request,
          forwarding_type: forwardingType,
        } as CreatePolicyStep1Request);
        setEditingId(result.id);
        setMode("edit");
        await refetch();
      } else if (editingId) {
        await updateStep1Mutation.mutateAsync({
          policyId: editingId,
          request: request as UpdatePolicyStep1Request,
        });
        await refetch();
      }
      return true;
    } catch {
      // toast는 mutation에서 처리
      return false;
    }
  };

  const saveStep2 = async (): Promise<boolean> => {
    if (!validateStep2()) return false;
    if (!editingId) {
      setErrorMsg("1단계 데이터가 저장되지 않았습니다.");
      return false;
    }

    let request: UpdatePolicyStep2Request;

    if (forwardingType === "s3") {
      request = {
        forwarding_type: "s3",
        bucket_region: bucketRegion.trim(),
        bucket_name: bucketName.trim(),
        role_arn: roleArn.trim(),
        prefix: prefix.trim() || undefined,
        external_id: externalId.trim(),
      } as UpdatePolicyStep2S3Request;
    } else {
      request = {
        forwarding_type: "gcs",
        bucket_name: bucketName.trim(),
        target_service_account: targetServiceAccount.trim(),
        prefix: prefix.trim() || undefined,
      } as UpdatePolicyStep2GCSRequest;
    }

    try {
      await updateStep2Mutation.mutateAsync({
        policyId: editingId,
        request,
      });
      await refetch();
      return true;
    } catch {
      // toast는 mutation에서 처리
      return false;
    }
  };

  const runConnectionTest = async () => {
    setErrorMsg("");
    if (!editingId) {
      setErrorMsg("정책 ID가 없습니다.");
      return;
    }

    setConnectionTestStatus("testing");
    setConnectionTestMessage("연결을 테스트 중입니다...");

    let request: ConnectionTestRequest;

    if (forwardingType === "s3") {
      request = {
        forwarding_type: "s3",
        bucket_name: bucketName.trim(),
        bucket_region: bucketRegion.trim(),
        role_arn: roleArn.trim(),
        prefix: prefix.trim() || undefined,
        external_id: externalId.trim(),
      };
    } else {
      request = {
        forwarding_type: "gcs",
        bucket_name: bucketName.trim(),
        target_service_account: targetServiceAccount.trim(),
        prefix: prefix.trim() || undefined,
      };
    }

    try {
      const result = await testConnectionMutation.mutateAsync({
        policyId: editingId,
        request,
      });
      setConnectionTestStatus(result.status as DataForwarderConnectionTestStatus);
      setConnectionTestMessage(result.message);
      if (result.status === "success") {
        await refetch();
      }
    } catch {
      setConnectionTestStatus("failed");
      setConnectionTestMessage("연결 테스트에 실패했습니다.");
    }
  };

  const finalize = () => {
    if (connectionTestStatus !== "success") {
      setErrorMsg("연결 테스트를 성공적으로 완료해야 합니다.");
      return;
    }
    setOpen(false);
    resetForm();
  };

  const next = async () => {
    if (step === 1) {
      const ok = await saveStep1();
      if (ok) {
        setExternalId(md5(policyName.trim()));
        setStep(2);
      }
      return;
    }
    if (step === 2) {
      const ok = await saveStep2();
      if (ok) setStep(3);
      return;
    }
  };

  const prev = () => {
    if (step === 3 && editingId) {
      // 3단계에서 2단계로 돌아갈 때 최신 정책 데이터에서 step2 필드 재로딩
      const p = policies.find((x) => x.id === editingId);
      if (p) {
        const rawDestInfo = p.destination_info;
        const destInfo: Record<string, unknown> | null =
          typeof rawDestInfo === "string"
            ? (() => {
                try {
                  return JSON.parse(rawDestInfo) as Record<string, unknown>;
                } catch {
                  return null;
                }
              })()
            : (rawDestInfo ?? null);

        setForwardingType(p.forwarding_type ?? "s3");
        if (
          destInfo &&
          (p.forwarding_type === "s3" || (!p.forwarding_type && forwardingType === "s3"))
        ) {
          const d = destInfo as Record<string, string>;
          setExternalId(d.external_id || md5((p.policy_name ?? "").trim()));
          setBucketRegion(d.bucket_region ?? "");
          setBucketName(d.bucket_name ?? "");
          setRoleArn(d.role_arn ?? "");
          setPrefix(d.prefix ?? d.s3_path_prefix ?? "");
        } else if (destInfo && p.forwarding_type === "gcs") {
          const d = destInfo as Record<string, string>;
          setBucketName(d.bucket_name ?? "");
          setTargetServiceAccount(d.target_service_account ?? "");
          setPrefix(d.prefix ?? "");
        }
      }
    }
    setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s));
  };

  const deletePolicies = async (ids: number[]) => {
    await deleteMutation.mutateAsync(ids);
    // Invalidate queries to refresh the list
    queryClient.invalidateQueries({ queryKey: queryKeys.dataForwarderPolicies.list() });
  };

  return {
    policies,
    channels,
    organizations,
    regions,
    errorMsg,
    setErrorMsg,
    reload,
    deletePolicies,
    isLoading,

    // wizard state + actions
    open,
    setOpen,
    step,
    mode,
    openCreate,
    openEdit,
    next,
    prev,
    finalize,

    // step fields
    policyName,
    setPolicyName,
    orgId,
    setOrgId,
    workspaceId,
    setWorkspaceId,
    channel,
    setChannel: handleChannelChange,
    subChannel,
    setSubChannel,
    dwAccountId,
    setDwAccountId,
    reports,
    setReports,
    externalId,

    forwardingType,
    setForwardingType,
    bucketRegion,
    setBucketRegion,
    bucketName,
    setBucketName,
    roleArn,
    setRoleArn,
    prefix,
    setPrefix,
    targetServiceAccount,
    setTargetServiceAccount,

    connectionTestStatus,
    connectionTestMessage,
    runConnectionTest,
  };
}
